import {
  type DiscordGuildChannel,
  DISCORD_GUILD_CHANNEL_CLIENT_COLUMNS_CSV,
  DiscordGuildChannelRowResponseSchema,
} from '@playerbase/types';

import { supabase } from '../lib/supabase.js';

// ---------------------------------------------------------------------------
// Channel-picker data layer.
//
// All reads come from the bot-maintained discord_guild_channels mirror
// (table added in migration 0013) via direct supabase-js calls under RLS.
// Mutations go through security-definer RPCs so failures (not a member,
// stale channel id, disconnected connection) raise explicit errors the
// picker can surface, rather than silently affecting zero rows.
// ---------------------------------------------------------------------------

export async function fetchDiscordGuildChannels(
  connectionId: string,
): Promise<DiscordGuildChannel[]> {
  const { data, error } = await supabase
    .from('discord_guild_channels')
    .select(DISCORD_GUILD_CHANNEL_CLIENT_COLUMNS_CSV)
    .eq('connection_id', connectionId)
    .order('parent_name', { ascending: true, nullsFirst: true })
    .order('position', { ascending: true })
    .order('name', { ascending: true });

  if (error !== null) {
    console.warn('[discord-channels-data] fetch failed:', error.message);
    return [];
  }
  if (data === null) return [];

  const out: DiscordGuildChannel[] = [];
  for (const row of data) {
    const parsed = DiscordGuildChannelRowResponseSchema.safeParse(row);
    if (parsed.success) {
      out.push(parsed.data);
    } else {
      console.warn(
        '[discord-channels-data] could not parse row:',
        parsed.error.issues,
      );
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// update_game_watched_channels RPC (migration 0082). Replaces the entire
// per-game array each call - no add/remove deltas; the picker always has
// the full intent in front of the user.
//
// Replaces the connection-scoped update_discord_watched_channels from
// migration 0013; the old RPC was dropped in 0082 as the schema moved
// watched channels off discord_connections onto game_watched_channels.
// ---------------------------------------------------------------------------

export type UpdateWatchedChannelsError =
  | 'not_authenticated'
  | 'not_a_member'
  | 'game_not_found'
  | 'game_not_connected'
  | 'connection_disconnected'
  | 'unknown_channel_id'
  | 'channel_ids_required'
  | 'unknown';

export type UpdateWatchedChannelsResult =
  | { ok: true }
  | { ok: false; code: UpdateWatchedChannelsError };

export async function updateGameWatchedChannels(
  gameId: string,
  channelIds: string[],
): Promise<UpdateWatchedChannelsResult> {
  const { error } = await supabase.rpc('update_game_watched_channels', {
    p_game_id: gameId,
    p_channel_ids: channelIds,
  });
  if (error === null) return { ok: true };

  console.warn('[discord-channels-data] update watched failed:', error.message);
  for (const code of [
    'not_authenticated',
    'not_a_member',
    'game_not_found',
    'game_not_connected',
    'connection_disconnected',
    'unknown_channel_id',
    'channel_ids_required',
  ] as const) {
    if (error.message.includes(code)) {
      return { ok: false, code };
    }
  }
  return { ok: false, code: 'unknown' };
}

// ---------------------------------------------------------------------------
// get_studio_channel_watchers RPC (migration 0082). One row per channel
// any game in the studio watches. Powers the picker's "also watched by
// [Other Game]" hint and the per-game watched set for the active game.
// ---------------------------------------------------------------------------

export interface StudioChannelWatcher {
  discordChannelId: string;
  thisGameWatches: boolean;
  otherGameSlugs: string[];
}

export async function fetchStudioChannelWatchers(
  gameId: string,
): Promise<StudioChannelWatcher[]> {
  const { data, error } = await supabase.rpc('get_studio_channel_watchers', {
    p_game_id: gameId,
  });
  if (error !== null) {
    console.warn(
      '[discord-channels-data] get_studio_channel_watchers failed:',
      error.message,
    );
    return [];
  }
  if (!Array.isArray(data)) return [];
  const out: StudioChannelWatcher[] = [];
  for (const row of data) {
    const r = row as Record<string, unknown>;
    const discordChannelId =
      typeof r['discord_channel_id'] === 'string'
        ? (r['discord_channel_id'] as string)
        : null;
    if (discordChannelId === null) continue;
    const thisGameWatches = Boolean(r['this_game_watches']);
    const others = Array.isArray(r['other_game_slugs'])
      ? (r['other_game_slugs'] as unknown[]).filter((s): s is string => typeof s === 'string')
      : [];
    out.push({ discordChannelId, thisGameWatches, otherGameSlugs: others });
  }
  return out;
}

// ---------------------------------------------------------------------------
// request_discord_channels_resync RPC: picker's manual Refresh button.
// Returns immediately - the bot's resync writes show up via Realtime.
// ---------------------------------------------------------------------------

export async function requestDiscordChannelsResync(connectionId: string): Promise<void> {
  const { error } = await supabase.rpc('request_discord_channels_resync', {
    p_connection_id: connectionId,
  });
  if (error !== null) {
    console.warn('[discord-channels-data] resync request failed:', error.message);
  }
}

// ---------------------------------------------------------------------------
// request_discord_channel_backfill RPC (game-scoped overload, migration
// 0118): fired when the user picks N>0 in the AddChannelModal's "Bring over
// history going back N days" input. Inserts a discord_backfill_job row and
// notifies the bot; the bot updates that row as it imports so the portal can
// show start / finish / fail via the shell's job-activity subscription. The
// portal call stays fire-and-forget - the live status comes through realtime,
// not this promise.
// ---------------------------------------------------------------------------

export type RequestChannelBackfillError =
  | 'not_authenticated'
  | 'not_a_member'
  | 'game_not_found'
  | 'game_not_connected'
  | 'channel_ids_required'
  | 'days_out_of_range'
  | 'unknown';

export type RequestChannelBackfillResult =
  | { ok: true }
  | { ok: false; code: RequestChannelBackfillError };

export async function requestChannelBackfill(
  connectionId: string,
  gameId: string,
  channelIds: string[],
  days: number,
): Promise<RequestChannelBackfillResult> {
  const { error } = await supabase.rpc('request_discord_channel_backfill', {
    p_connection_id: connectionId,
    p_game_id: gameId,
    p_channel_ids: channelIds,
    p_days: days,
  });
  if (error === null) return { ok: true };

  console.warn('[discord-channels-data] backfill request failed:', error.message);
  for (const code of [
    'not_authenticated',
    'not_a_member',
    'game_not_found',
    'game_not_connected',
    'channel_ids_required',
    'days_out_of_range',
  ] as const) {
    if (error.message.includes(code)) {
      return { ok: false, code };
    }
  }
  return { ok: false, code: 'unknown' };
}
